import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import TrustBadge from "@/components/TrustBadge";
import TrustRadar from "@/components/TrustRadar";
import HospitalCommandTabs from "@/components/hospital/CommandTabs";
import TrustBadgeEmbed from "@/components/hospital/TrustBadgeEmbed";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";

export default function HospitalTrust() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const me = await api.get("/hospital/me");
      const trust = await api.get(`/trust-index/hospital/${me.data.id}`);
      setData({ hospital: me.data, trust: trust.data });
    })();
  }, []);

  if (!data) return <AppShell pageTitle="Trust Index"><div className="text-sm text-muted-foreground">Loading…</div></AppShell>;

  return (
    <AppShell
      pageTitle="Healthcare Trust Index"
      pageDescription="A percentile score based on 11 objective criteria. Improve any dimension to lift your overall tier."
      actions={<TrustBadge tier={data.trust.tier} score={data.trust.overall_score} />}
    >
      <HospitalCommandTabs />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-border bg-white p-6">
          <div className="overline mb-3">11-criteria radar</div>
          <TrustRadar criteria={data.trust.criteria} height={380} />
        </div>
        <div className="rounded-lg border border-border bg-white p-6">
          <div className="overline mb-3">Overall</div>
          <div className="font-serif text-6xl text-primary">{data.trust.overall_score}</div>
          <div className="mt-1 text-sm text-muted-foreground">Percentile score</div>
          <div className="mt-6 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Current tier</span>
              <TrustBadge tier={data.trust.tier} score={data.trust.overall_score} size="sm" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Next tier at</span>
              <span className="font-medium text-primary">{nextTierAt(data.trust.overall_score)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-border bg-white p-6">
        <h2 className="font-serif text-2xl text-primary mb-4">Criterion scores</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="criteria-scores">
          {data.trust.criteria.map((c) => (
            <div key={c.name}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-primary">{c.name}</span>
                <span className="text-muted-foreground">{c.score}/100</span>
              </div>
              <Progress value={c.score} className="mt-2 h-1.5" />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-border bg-primary text-primary-foreground p-6">
        <div className="overline text-primary-foreground/70">Priority improvements</div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.trust.roadmap.map((r) => (
            <div key={r.area} className="rounded-md bg-primary-foreground/10 p-4">
              <div className="text-sm font-medium">{r.area}</div>
              <div className="text-xs opacity-80 mt-1">{r.current} → {r.target}</div>
              <p className="text-xs opacity-90 mt-2">{r.recommendation}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <TrustBadgeEmbed hospitalId={data.hospital.id} />
      </div>
    </AppShell>
  );
}

function nextTierAt(score) {
  if (score >= 88) return "Already at Platinum";
  if (score >= 78) return "88 (Platinum)";
  if (score >= 65) return "78 (Gold)";
  return "65 (Silver)";
}
